import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { PropTypes } from "prop-types";
import api from "../services/api";

export const AuthContext = createContext();

export function AuthContextProvider({ children }) {
    const [isAuth, setIsAuth] = useState(!!localStorage.getItem("token"));
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const hydrateAuthMe = useCallback(async () => {
        setLoading(true);
        const token = localStorage.getItem("token");
        if (!token) {
            setUser(null);
            setIsAuth(false);
            setLoading(false);
            return;
        }

        try {
            const { data } = await api.get('/auth/me');
            setUser({
                id: data.id,
                username: data.username,
                email: data.email,
                role: data.role,
                permissions: data.permissions || [],
                modules: data.modules || [],
            });
            setIsAuth(true);
            localStorage.setItem('rolUsuario', data.role || '');
        } catch (error) {
            localStorage.removeItem("token");
            setUser(null);
            setIsAuth(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        hydrateAuthMe();
    }, [hydrateAuthMe]);

    const loginin = useCallback((token) => {
        localStorage.setItem("token", token);
        setIsAuth(true);
        hydrateAuthMe();
    }, [hydrateAuthMe]);
    

    const logout = useCallback(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("idUsuario");
        localStorage.removeItem("nombreUsuario");
        localStorage.removeItem("rolUsuario");
        localStorage.removeItem("zonasUsuario");
        localStorage.removeItem("zonaActiva");
        localStorage.removeItem("idZonaActiva");
        setUser(null);
        setIsAuth(false);
        window.location.href = '/login';
    }, []);

    const can = useCallback((permission) => {
        if (!permission) return true;
        const permissions = user?.permissions || [];
        return permissions.includes(permission);
    }, [user]);

    const value = useMemo(() => ({
        loginin,
        logout,
        isAuth,
        user,
        role: user?.role || null,
        permissions: user?.permissions || [],
        modules: user?.modules || [],
        can,
        refreshSession: hydrateAuthMe,
        loading,
    }), [loginin, logout, isAuth, user, can, hydrateAuthMe, loading]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthContextProvider.propTypes = {
    children: PropTypes.node.isRequired
};

export function useAuthContext() {
    return useContext(AuthContext);
}